import React, { useState } from 'react';
import { Box, Divider, MenuItem, Select, Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { AudioDiagnostic } from '../AudioDiagnostic';
import { VideoPreview } from '../VideoPreview';
import { MasterVolume } from './MasterVolume';
import { VideoInputList } from './VideoInputList';
import { AudioInputList } from './AudioInputList';
import { AudioOutputList } from './AudioOutputList';
import { LayoutList } from './LayoutList';
import { BackgroundEffectsList } from 'components';
import { ChevronRightIcon } from 'assets';
import usFlag from 'assets/images/us-flag.svg';
import mxFlag from 'assets/images/mx-flag.svg';
import brFlag from 'assets/images/br-flag.svg';
import frFlag from 'assets/images/fr-flag.svg';
import zhFlag from 'assets/images/zh-flag.svg';
import ruFlag from 'assets/images/ru-flag.svg';
import hiFlag from 'assets/images/hi-flag.svg';
import arFlag from 'assets/images/ar-flag.svg';
import { createThemeOptions, getVisibleThemePreference, renderThemeIcon, visibleThemePreferences } from 'theme';
import { useThemePreference } from 'hooks';

import styles from './DeviceSelectionContainer.module.scss';

type DeviceSelectionContainerProps = {
  withVideoPreview?: boolean;
  withMasterVolume?: boolean;
  withBackground?: boolean;
  withLayoutSelection?: boolean;
  withThemeSelection?: boolean;
  withLanguageSelection?: boolean;
  withAudioDiagnostic?: boolean;
  inlineAudioDiagnostic?: boolean;
  microphoneFirst?: boolean;
  forceLightTheme?: boolean;
  viewAtBottom?: boolean;
};

export function DeviceSelectionContainer({
  withVideoPreview,
  withMasterVolume,
  withBackground,
  withLayoutSelection,
  withThemeSelection,
  withLanguageSelection,
  withAudioDiagnostic,
  inlineAudioDiagnostic,
  microphoneFirst,
  forceLightTheme,
  viewAtBottom,
}: DeviceSelectionContainerProps) {
  const { t, i18n } = useTranslation('providerRoom');
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const { themePreference, setThemePreference } = useThemePreference();
  const themePreferenceForUi = getVisibleThemePreference(themePreference);

  const handleThemeSelect = (mode: typeof themePreference) => {
    setThemePreference(mode);
  };

  const selectedLanguage =
    i18n.resolvedLanguage === 'es' ||
    i18n.resolvedLanguage === 'br' ||
    i18n.resolvedLanguage === 'fr' ||
    i18n.resolvedLanguage === 'zh' ||
    i18n.resolvedLanguage === 'ru' ||
    i18n.resolvedLanguage === 'hi' ||
    i18n.resolvedLanguage === 'ar'
      ? i18n.resolvedLanguage
      : 'en';
  const activeLanguage = (i18n.resolvedLanguage || i18n.language || selectedLanguage || 'en').toLowerCase();
  const isRtl = activeLanguage === 'ar' || activeLanguage.startsWith('ar-') || i18n.dir() === 'rtl';
  const languageOptions: Array<{
    code: 'en' | 'es' | 'br' | 'fr' | 'zh' | 'ru' | 'hi' | 'ar';
    label: string;
    flag: string;
  }> = [
    { code: 'en', label: 'English', flag: usFlag },
    { code: 'es', label: 'Español', flag: mxFlag },
    { code: 'br', label: 'Português', flag: brFlag },
    { code: 'fr', label: 'Français', flag: frFlag },
    { code: 'zh', label: '中文', flag: zhFlag },
    { code: 'ru', label: 'Русский', flag: ruFlag },
    { code: 'hi', label: 'हिन्दी', flag: hiFlag },
    { code: 'ar', label: 'العربية', flag: arFlag },
  ];
  const selectedLanguageOption =
    languageOptions.find((option) => option.code === selectedLanguage) || languageOptions[0];
  const themeOptions = createThemeOptions({
    light: t('light', { defaultValue: 'Light' }),
    dark: t('dark', { defaultValue: 'Dark' }),
    system: t('system', { defaultValue: 'System' }),
    calm: t('calm', { defaultValue: 'Calm' }),
    nature: t('nature', { defaultValue: 'Nature' }),
  }).filter((option) => visibleThemePreferences.includes(option.mode));
  const selectedThemeOption = themeOptions.find((option) => option.mode === themePreferenceForUi) || themeOptions[0];

  const handleLanguageSelect = (language: 'en' | 'es' | 'br' | 'fr' | 'zh' | 'ru' | 'hi' | 'ar') => {
    i18n.changeLanguage(language);
  };

  const microphoneHeader = (
    <Stack direction='row' alignItems='center' justifyContent='flex-start' className={styles.microphoneHeader}>
      <Typography
        component='p'
        variant={isDesktop ? 'body5' : 'body2'}
        sx={forceLightTheme ? { color: 'var(--app-text-default)' } : undefined}
      >
        {t('microphone')}
      </Typography>
      {withAudioDiagnostic && inlineAudioDiagnostic && (
        <Box className={styles.inlineAudioDiagnostic}>
          <AudioDiagnostic compact />
        </Box>
      )}
    </Stack>
  );

  const themeSection = withThemeSelection && (
    <>
      <Divider />

      <Stack className={styles.row}>
        <Stack direction='row' alignItems='center'>
          <Typography
            component='p'
            variant={isDesktop ? 'body5' : 'body2'}
            sx={forceLightTheme ? { color: 'var(--app-text-default)' } : undefined}
          >
            {t('theme')}
          </Typography>
        </Stack>
        <Select
          fullWidth
          variant='outlined'
          IconComponent={ChevronRightIcon}
          value={themePreferenceForUi}
          renderValue={() => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: isRtl ? 'row-reverse' : 'row',
                justifyContent: 'flex-start',
                gap: '8px',
                direction: 'ltr',
              }}
            >
              {renderThemeIcon(selectedThemeOption.mode)}
              <span>{selectedThemeOption.label}</span>
            </Box>
          )}
          onChange={(e) => handleThemeSelect(e.target.value as typeof themePreference)}
          MenuProps={
            forceLightTheme
              ? {
                  PaperProps: {
                    sx: {
                      backgroundColor: 'var(--app-field-surface)',
                      color: 'var(--app-text-default)',
                      '& .MuiMenuItem-root': {
                        color: 'var(--app-text-default)',
                      },
                    },
                  },
                }
              : undefined
          }
          sx={{
            ...(forceLightTheme && {
              backgroundColor: 'var(--app-field-surface) !important',
              color: 'var(--app-text-default)',
              '& .MuiSelect-select': {
                backgroundColor: 'var(--app-field-surface) !important',
                color: 'var(--app-text-default) !important',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--app-field-outline) !important',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--app-field-outline) !important',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--app-field-outline) !important',
              },
              '& .MuiSelect-icon': {
                color: 'var(--app-text-muted)',
              },
            }),
            '& svg': {
              width: '10px',
            },
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        >
          {themeOptions.map((option) => (
            <MenuItem
              key={option.mode}
              value={option.mode}
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: isRtl ? 'row-reverse' : 'row',
                justifyContent: 'flex-start',
                gap: '8px',
                direction: 'ltr',
                textAlign: isRtl ? 'right' : 'left',
              }}
            >
              {renderThemeIcon(option.mode)}
              <span>{option.label}</span>
            </MenuItem>
          ))}
        </Select>
      </Stack>
    </>
  );

  const layoutSection = withLayoutSelection && (
    <>
      <Divider />

      <Stack className={styles.row}>
        <Stack direction='row' alignItems='center'>
          <Typography
            component='p'
            variant={isDesktop ? 'body5' : 'body2'}
            sx={forceLightTheme ? { color: 'var(--app-text-default)' } : undefined}
          >
            {t('view')}
          </Typography>
        </Stack>
        <LayoutList forceLightTheme={forceLightTheme} />
      </Stack>
    </>
  );

  const languageSection = withLanguageSelection && (
    <>
      <Divider />

      <Stack className={styles.row}>
        <Stack direction='row' alignItems='center'>
          <Typography
            component='p'
            variant={isDesktop ? 'body5' : 'body2'}
            sx={forceLightTheme ? { color: 'var(--app-text-default)' } : undefined}
          >
            {t('languageLabel')}
          </Typography>
        </Stack>
        <Select
          fullWidth
          variant='outlined'
          IconComponent={ChevronRightIcon}
          value={selectedLanguage}
          renderValue={() => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: isRtl ? 'row-reverse' : 'row',
                justifyContent: 'flex-start',
                gap: '8px',
                direction: 'ltr',
              }}
            >
              <img src={selectedLanguageOption.flag} alt='' width={18} height={12} />
              <span>{selectedLanguageOption.label}</span>
            </Box>
          )}
          onChange={(e) =>
            handleLanguageSelect(e.target.value as 'en' | 'es' | 'br' | 'fr' | 'zh' | 'ru' | 'hi' | 'ar')
          }
          MenuProps={
            forceLightTheme
              ? {
                  PaperProps: {
                    sx: {
                      backgroundColor: 'var(--app-field-surface)',
                      color: 'var(--app-text-default)',
                      '& .MuiMenuItem-root': {
                        color: 'var(--app-text-default)',
                      },
                    },
                  },
                }
              : undefined
          }
          sx={{
            ...(forceLightTheme && {
              backgroundColor: 'var(--app-field-surface) !important',
              color: 'var(--app-text-default)',
              '& .MuiSelect-select': {
                backgroundColor: 'var(--app-field-surface) !important',
                color: 'var(--app-text-default) !important',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--app-field-outline) !important',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--app-field-outline) !important',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--app-field-outline) !important',
              },
              '& .MuiSelect-icon': {
                color: 'var(--app-text-muted)',
              },
            }),
            '& svg': {
              width: '10px',
            },
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        >
          {languageOptions.map((option) => (
            <MenuItem
              key={option.code}
              value={option.code}
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: isRtl ? 'row-reverse' : 'row',
                justifyContent: 'flex-start',
                gap: '8px',
                direction: 'ltr',
                textAlign: isRtl ? 'right' : 'left',
              }}
            >
              <img src={option.flag} alt='' width={18} height={12} />
              <span>{option.label}</span>
            </MenuItem>
          ))}
        </Select>
      </Stack>
    </>
  );

  return (
    <Box className={styles.container}>
      {withMasterVolume && (
        <>
          <Stack className={styles.row}>
            <Stack direction='row' alignItems='center'>
              <Typography
                component='p'
                variant={isDesktop ? 'body5' : 'body2'}
                sx={forceLightTheme ? { color: 'var(--app-text-default)' } : undefined}
              >
                {t('meetingAudioVolume')}
              </Typography>
            </Stack>

            <MasterVolume />
          </Stack>
          <Divider />
        </>
      )}

      {withVideoPreview && <VideoPreview className={styles.preview} />}

      {withAudioDiagnostic && !inlineAudioDiagnostic && <AudioDiagnostic />}

      {microphoneFirst ? (
        <>
          <Stack className={styles.row}>
            {microphoneHeader}
            <AudioInputList forceLightTheme={forceLightTheme} />
          </Stack>
          <Stack className={styles.row}>
            <Stack direction='row' alignItems='center'>
              <Typography
                component='p'
                variant={isDesktop ? 'body5' : 'body2'}
                sx={forceLightTheme ? { color: 'var(--app-text-default)' } : undefined}
              >
                {t('camera')}
              </Typography>
            </Stack>
            <VideoInputList forceLightTheme={forceLightTheme} />
          </Stack>
        </>
      ) : (
        <>
          <Stack className={styles.row}>
            <Stack direction='row' alignItems='center'>
              <Typography
                component='p'
                variant={isDesktop ? 'body5' : 'body2'}
                sx={forceLightTheme ? { color: 'var(--app-text-default)' } : undefined}
              >
                {t('camera')}
              </Typography>
            </Stack>
            <VideoInputList forceLightTheme={forceLightTheme} />
          </Stack>

          <Stack className={styles.row}>
            {microphoneHeader}
            <AudioInputList forceLightTheme={forceLightTheme} />
          </Stack>
        </>
      )}

      <Stack className={styles.row}>
        <Stack direction='row' alignItems='center'>
          <Typography
            component='p'
            variant={isDesktop ? 'body5' : 'body2'}
            sx={forceLightTheme ? { color: 'var(--app-text-default)' } : undefined}
          >
            {t('speaker')}
          </Typography>
        </Stack>
        <AudioOutputList forceLightTheme={forceLightTheme} />
      </Stack>

      {viewAtBottom ? (
        <>
          {themeSection}
          {languageSection}
          {layoutSection}
        </>
      ) : (
        <>
          {layoutSection}
          {themeSection}
          {languageSection}
        </>
      )}

      {withBackground && (
        <>
          <Divider />

          <Stack className={styles.row}>
            <Stack direction='row' alignItems='center'>
              <Typography
                component='p'
                variant={isDesktop ? 'body5' : 'body2'}
                sx={forceLightTheme ? { color: 'var(--app-text-default)' } : undefined}
              >
                {t('chooseVirtualBackground')}
              </Typography>
            </Stack>
            <BackgroundEffectsList itemClassName={styles.backgroundEffectsListItem} />
          </Stack>
        </>
      )}
    </Box>
  );
}
